import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiCheckCircle, FiArrowLeft, FiGlobe, FiSearch, FiAlertTriangle, FiAlertCircle, FiXCircle } from 'react-icons/fi';
import { datosAPI, flujoAPI } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import HistoricidadRoleSelector from '../components/HistoricidadRoleSelector';

const BandejaEditor = () => {
  const [datos, setDatos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [modalAbierto, setModalAbierto] = useState(false);
  const [datoSeleccionado, setDatoSeleccionado] = useState(null);
  const [accion, setAccion] = useState('');
  const [motivo, setMotivo] = useState('');
  const [seleccionados, setSeleccionados] = useState([]);
  const [modoMasivo, setModoMasivo] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const { hasRole, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!hasRole('editor')) {
      navigate('/');
      return;
    }
  }, []);

  // Cargar datos solo cuando se selecciona el rol de editor
  useEffect(() => {
    if (selectedRole === 'editor') {
      cargarDatos();
    }
  }, [selectedRole]);

  const handleRoleChange = (role) => {
    setSelectedRole(role);
    
    // Si el rol seleccionado no es editor, redirigir a la bandeja correspondiente
    if (role === 'gestor') {
      navigate('/historicidad/gestor');
    } else if (role === 'validador') {
      navigate('/historicidad/validador');
    } else if (role === 'historiador') {
      navigate('/historicidad/gestor/nuevo');
    }
  };

  const cargarDatos = async () => {
    try {
      setLoading(true);
      const params = { search: searchTerm };
      const response = await datosAPI.getBandeja(params);
      setDatos(response.data.data);
    } catch (error) {
      console.error('Error al cargar bandeja:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!loading) cargarDatos();
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const toggleSeleccion = (datoId) => {
    setSeleccionados(prev => 
      prev.includes(datoId) 
        ? prev.filter(id => id !== datoId)
        : [...prev, datoId]
    );
  };

  const toggleTodos = () => {
    if (seleccionados.length === datos.length) {
      setSeleccionados([]);
    } else {
      setSeleccionados(datos.map(d => d.id));
    }
  };

  const abrirModal = (dato, tipoAccion) => {
    setDatoSeleccionado(dato);
    setAccion(tipoAccion);
    setMotivo('');
    setModoMasivo(false);
    setModalAbierto(true);
  };

  const abrirModalMasivo = (tipoAccion) => {
    if (seleccionados.length === 0) {
      alert('Selecciona al menos un registro');
      return;
    }
    setDatoSeleccionado(null);
    setAccion(tipoAccion);
    setMotivo('');
    setModoMasivo(true);
    setModalAbierto(true);
  };

  const cerrarModal = () => {
    setModalAbierto(false);
    setDatoSeleccionado(null);
    setAccion('');
    setMotivo('');
    setModoMasivo(false);
  };

  const confirmarAccion = async () => {
    if (!modoMasivo && !datoSeleccionado) return;
    if (modoMasivo && seleccionados.length === 0) return;

    try {
      let estadoDestino = '';
      let motivoTexto = motivo;

      switch (accion) {
        case 'publicar':
          estadoDestino = 'informacion_aprobada';
          motivoTexto = motivoTexto || 'Información aprobada y lista para publicación.';
          break;
        case 'devolver':
          estadoDestino = 'informacion_valida';
          if (!motivoTexto.trim()) {
            alert('Debes proporcionar un motivo para devolver');
            return;
          }
          break;
        case 'pendiente':
          estadoDestino = 'pendiente';
          if (!motivoTexto.trim()) {
            alert('Debes proporcionar un motivo para marcar como pendiente');
            return;
          }
          break;
        case 'rechazar':
          estadoDestino = 'rechazada';
          if (!motivoTexto.trim()) {
            alert('Debes proporcionar un motivo detallado para rechazar (ej: plagio, información falsa, etc.)');
            return;
          }
          break;
      }

      if (modoMasivo) {
        // Procesar acciones masivas
        let exitosos = 0;
        let fallidos = 0;

        for (const datoId of seleccionados) {
          try {
            await flujoAPI.transicionar(datoId, estadoDestino, motivoTexto);
            exitosos++;
          } catch (error) {
            console.error(`Error al procesar dato ${datoId}:`, error);
            fallidos++;
          }
        }

        alert(`Acción masiva completada:\n✅ Exitosos: ${exitosos}\n❌ Fallidos: ${fallidos}`);
        setSeleccionados([]);
      } else {
        // Acción individual
        await flujoAPI.transicionar(datoSeleccionado.id, estadoDestino, motivoTexto);
        alert('Acción completada exitosamente');
      }
      
      cerrarModal();
      cargarDatos();
    } catch (error) {
      console.error('Error al procesar acción:', error);
      alert(error.response?.data?.message || 'Error al procesar la acción');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-teal-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2">Bandeja del Editor</h1>
              <p className="text-green-100">
                {selectedRole === 'editor' ? `${datos.length} ${datos.length === 1 ? 'dato pendiente' : 'datos pendientes'} de revisión final` : 'Selecciona tu rol para continuar'}
              </p>
            </div>
            {selectedRole === 'editor' && datos.length > 0 && (
              <div className="bg-white text-green-600 rounded-full h-16 w-16 flex items-center justify-center font-bold text-2xl">
                {datos.length}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Selector de Rol */}
        <HistoricidadRoleSelector
          selectedRole={selectedRole}
          onRoleChange={handleRoleChange}
        />

        {/* Solo mostrar contenido si hay un rol seleccionado y es 'editor' */}
        {!selectedRole && (
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <FiAlertTriangle className="h-5 w-5 text-yellow-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-yellow-700">
                  Por favor, selecciona un rol para continuar operando en este módulo.
                </p>
              </div>
            </div>
          </div>
        )}

        {selectedRole === 'editor' && (
          <>
            {/* Búsqueda */}
            <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Buscar por tema o palabras clave..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
            />
          </div>
        </div>

        {/* Acciones masivas */}
        {datos.length > 0 && (
          <div className="bg-white rounded-lg shadow p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={seleccionados.length === datos.length && datos.length > 0}
                    onChange={toggleTodos}
                    className="w-5 h-5 text-green-600 rounded focus:ring-green-500"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-700">
                    Seleccionar todos ({seleccionados.length}/{datos.length})
                  </span>
                </label>
              </div>
              {seleccionados.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => abrirModalMasivo('publicar')}
                    className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-sm font-medium"
                  >
                    <FiGlobe className="inline mr-1" size={14} />
                    Publicar {seleccionados.length} registros
                  </button>
                  <button
                    onClick={() => abrirModalMasivo('devolver')}
                    className="px-3 py-1.5 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition text-sm font-medium"
                  >
                    <FiArrowLeft className="inline mr-1" size={14} />
                    Devolver {seleccionados.length} registros
                  </button>
                  <button
                    onClick={() => abrirModalMasivo('pendiente')}
                    className="px-3 py-1.5 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition text-sm font-medium"
                  >
                    <FiAlertCircle className="inline mr-1" size={14} />
                    Marcar como Pendiente
                  </button>
                  <button
                    onClick={() => abrirModalMasivo('rechazar')}
                    className="px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition text-sm font-medium"
                  >
                    <FiXCircle className="inline mr-1" size={14} />
                    Rechazar {seleccionados.length} registros
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Lista de datos */}
        {datos.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <FiCheckCircle className="mx-auto text-green-500 mb-4" size={48} />
            <p className="text-gray-500 text-lg">¡Todo en orden! No tienes datos pendientes</p>
          </div>
        ) : (
          <div className="space-y-4">
            {datos.map((dato) => (
              <div key={dato.id} className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow">
                <div className="p-6">
                  {/* Checkbox de selección */}
                  <div className="flex items-start gap-3 mb-3">
                    <input
                      type="checkbox"
                      checked={seleccionados.includes(dato.id)}
                      onChange={() => toggleSeleccion(dato.id)}
                      className="w-5 h-5 text-green-600 rounded focus:ring-green-500 mt-1"
                    />
                    <div className="flex-1">
                  {/* Semáforo de alerta cuando fue devuelto */}
                  {dato.fueDevuelto && (
                    <div className="mb-4 bg-red-50 border-l-4 border-red-500 p-3 rounded">
                      <div className="flex items-start">
                        <FiAlertTriangle className="text-red-500 mt-0.5 mr-2 flex-shrink-0" size={20} />
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-red-800 mb-1">
                            🚨 Registro devuelto - Requiere atención especial
                          </p>
                          {dato.ultimaTransicion?.motivoTransicion && (
                            <p className="text-sm text-red-700">
                              <strong>Motivo:</strong> {dato.ultimaTransicion.motivoTransicion}
                            </p>
                          )}
                          {dato.ultimaTransicion?.usuario && (
                            <p className="text-xs text-red-600 mt-1">
                              Devuelto por: {dato.ultimaTransicion.usuario.nombre} ({dato.ultimaTransicion.usuario.rol})
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-xl font-bold text-gray-900">
                          {dato.tema}
                        </h3>
                        {dato.fueDevuelto && (
                          <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-semibold rounded-full">
                            DEVUELTO
                          </span>
                        )}
                      </div>
                      <p className="text-gray-600 text-sm mb-3">
                        {dato.descripcion}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {dato.catalogos?.map((cat) => (
                          <span
                            key={cat.id}
                            className="px-2 py-1 text-xs rounded"
                            style={{
                              backgroundColor: `${cat.color}20`,
                              color: cat.color
                            }}
                          >
                            {cat.nombre}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Porcentajes */}
                  <div className="grid grid-cols-4 gap-4 mb-4 p-4 bg-gradient-to-r from-blue-50 to-green-50 rounded-lg">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600">
                        {dato.porcentajeHistoricidad?.toFixed(1)}%
                      </div>
                      <div className="text-xs text-gray-500">Historicidad</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">
                        {dato.porcentajeAutenticidad?.toFixed(1)}%
                      </div>
                      <div className="text-xs text-gray-500">Autenticidad</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-purple-600">
                        {dato.porcentajeVeracidad?.toFixed(1)}%
                      </div>
                      <div className="text-xs text-gray-500">Veracidad</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-orange-600">
                        {((dato.porcentajeHistoricidad + dato.porcentajeAutenticidad + dato.porcentajeVeracidad) / 3).toFixed(1)}%
                      </div>
                      <div className="text-xs text-gray-500">Promedio</div>
                    </div>
                  </div>

                  {/* Info adicional */}
                  <div className="grid grid-cols-3 gap-4 text-sm text-gray-600 mb-4 bg-gray-50 p-3 rounded">
                    <div>
                      <span className="font-semibold">Investigación:</span>{' '}
                      {dato.tiempoInvestigacionHoras || 0} hrs
                    </div>
                    <div>
                      <span className="font-semibold">Prioridad:</span>{' '}
                      <span className={`px-2 py-0.5 rounded text-xs ${
                        dato.prioridad === 'alta' ? 'bg-red-100 text-red-800' :
                        dato.prioridad === 'media' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {dato.prioridad}
                      </span>
                    </div>
                    <div>
                      <span className="font-semibold">Criticidad:</span>{' '}
                      <span className={`px-2 py-0.5 rounded text-xs ${
                        dato.criticidad === 'alta' ? 'bg-red-100 text-red-800' :
                        dato.criticidad === 'media' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {dato.criticidad}
                      </span>
                    </div>
                  </div>

                  {/* Acciones */}
                  <div className="flex flex-wrap gap-2 border-t pt-4">
                    <Link
                      to={`/historicidad/editor/revisar/${dato.id}`}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm font-medium"
                    >
                      Ver Detalle Completo
                    </Link>
                    <button
                      onClick={() => abrirModal(dato, 'publicar')}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-sm font-medium"
                    >
                      <FiGlobe className="inline mr-1" /> Aprobar y Publicar
                    </button>
                    <button
                      onClick={() => abrirModal(dato, 'devolver')}
                      className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition text-sm font-medium"
                    >
                      <FiArrowLeft className="inline mr-1" /> Devolver a Validador
                    </button>
                    <button
                      onClick={() => abrirModal(dato, 'pendiente')}
                      className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition text-sm font-medium"
                    >
                      <FiAlertCircle className="inline mr-1" /> Marcar como Pendiente
                    </button>
                    <button
                      onClick={() => abrirModal(dato, 'rechazar')}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition text-sm font-medium"
                    >
                      <FiXCircle className="inline mr-1" /> Rechazar
                    </button>
                  </div>
                    </div> {/* Cierre del div flex-1 del checkbox */}
                  </div> {/* Cierre del div flex items-start gap-3 */}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modal de confirmación */}
        {modalAbierto && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4">
              {modoMasivo ? 'Acción Masiva: ' : ''}
              {accion === 'publicar' && 'Aprobar y Publicar'}
              {accion === 'devolver' && 'Devolver a Validador'}
              {accion === 'pendiente' && 'Marcar como Pendiente'}
              {accion === 'rechazar' && 'Rechazar dato'}
            </h3>

            {modoMasivo ? (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-blue-800">
                  ⚠️ Esta acción se aplicará a <strong>{seleccionados.length} registros</strong> seleccionados.
                  <br />
                  El motivo que ingreses se aplicará a todos los registros.
                </p>
              </div>
            ) : (
              <p className="text-gray-600 mb-4">
                Dato: <strong>{datoSeleccionado?.tema}</strong>
              </p>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {accion === 'publicar' && 'Comentarios finales (opcionales):'}
                {accion === 'devolver' && 'Motivo (requerido):'}
                {accion === 'pendiente' && 'Motivo (requerido):'}
                {accion === 'rechazar' && 'Motivo detallado (requerido):'}
              </label>
              <textarea
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                rows="4"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                placeholder={
                  accion === 'publicar'
                    ? 'Comentarios adicionales sobre la aprobación...'
                    : accion === 'devolver'
                    ? 'Explica por qué se devuelve al validador...'
                    : accion === 'pendiente'
                    ? 'Indica qué información falta o qué se está esperando...'
                    : 'Detalla el motivo del rechazo: plagio, información falsa, etc...'
                }
              />
            </div>

            {accion === 'publicar' && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-green-800">
                  ⚠️ Esta acción hará que el dato sea visible públicamente en la plataforma.
                </p>
              </div>
            )}

            {accion === 'pendiente' && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-orange-800">
                  📋 El dato quedará en tu bandeja esperando información adicional o cambios.
                </p>
              </div>
            )}

            {accion === 'rechazar' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-red-800">
                  ⚠️ Esta acción marcará el dato como rechazado. Especifica claramente el motivo.
                </p>
              </div>
            )}

            <div className="flex justify-end space-x-2">
              <button
                onClick={cerrarModal}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={confirmarAccion}
                className={`px-4 py-2 rounded-lg text-white ${
                  accion === 'publicar'
                    ? 'bg-green-600 hover:bg-green-700'
                    : accion === 'devolver'
                    ? 'bg-yellow-600 hover:bg-yellow-700'
                    : accion === 'pendiente'
                    ? 'bg-orange-600 hover:bg-orange-700'
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}
      </>
    )}
      </div>
    </div>
  );
};

export default BandejaEditor;

